import React, { useState } from "react";
import {
  Box,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";

/**
 * A component for rendering and editing a checkbox item
 */
const CheckboxItem = ({
  id,
  description,
  completed = false,
  onUpdate,
  onDelete,
  editable = true,
  draggable = true,
  isTemplateView = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [itemDescription, setItemDescription] = useState(description);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleToggleComplete = () => {
    onUpdate(id, { completed: !completed });
  };

  const handleTextChange = (e) => {
    setItemDescription(e.target.value);
  };

  const handleTextBlur = () => {
    setIsEditing(false);
    if (itemDescription !== description) {
      onUpdate(id, { description: itemDescription });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleTextBlur();
    }
  };

  const handleDelete = () => {
    onDelete(id);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1,
        mb: 1,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Grid
        container
        spacing={2}
        columns={24}
        sx={{ width: "100%", alignItems: "center" }}
      >
        {draggable && (
          <Grid size={{ xs: 1, sm: 1, md: 1 }}>
            <IconButton
              size="small"
              sx={{
                cursor: "grab",
                color: "text.disabled",
                mr: 0.5,
                visibility: isMobile ? "hidden" : "visible",
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Grid>
        )}
        <Grid size={{ xs: 22, sm: 22, md: 22 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            {isTemplateView && editable && isEditing ? (
              <TextField
                fullWidth
                variant="outlined"
                value={itemDescription}
                onChange={handleTextChange}
                onBlur={handleTextBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "inherit",
                    "& fieldset": {
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            ) : (
              <Box
                onClick={() => editable && isTemplateView && setIsEditing(true)}
                sx={{
                  cursor: editable ? "pointer" : "default",
                  wordBreak: "break-word",
                  fontWeight: "bold",
                  lineHeight: "normal",
                  minHeight: "2.5em",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "1.3em",
                  ml: 1.5,
                }}
              >
                {description || "Click to add description..."}
              </Box>
            )}
          </Box>
        </Grid>
        {editable && isTemplateView && (
          <Grid size={{ xs: 1, sm: 1, md: 1 }} sx={{ justifyContent: "right" }}>
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{ color: "error.light" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CheckboxItem;
